import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import "./listing-pagination.styles.scss";

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_MIN = 10;
export const PAGE_SIZE_MAX = 100;
export const PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE, 50, 100];

export function resolveReportsListingMeta(apiMeta, { page, pageSize }) {
  if (apiMeta == null) return null;

  const totalRecords =
    Number(apiMeta.total_records ?? apiMeta.total_count) || 0;
  const perPage = Number(pageSize || apiMeta.items_per_page) || DEFAULT_PAGE_SIZE;

  return {
    ...apiMeta,
    current_page: Number(page || apiMeta.current_page) || 1,
    total_records: totalRecords,
    items_per_page: perPage,
    total_pages: Math.max(1, Math.ceil(totalRecords / perPage) || 1)
  };
}

function ListingPagination({
  meta,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const currentPage = Number(meta?.current_page) || 1;
  const totalRecords = Number(meta?.total_records) || 0;
  const rawPerPage =
    pageSize || Number(meta?.items_per_page) || pageSizeOptions[0];
  const perPage = Math.max(
    PAGE_SIZE_MIN,
    Number(rawPerPage) || pageSizeOptions[0]
  );
  const pageSizeMenuOptions = pageSizeOptions.includes(perPage)
    ? pageSizeOptions
    : [...pageSizeOptions, perPage].sort((a, b) => a - b);
  const lastPage = Math.max(1, Math.ceil(totalRecords / perPage) || 1);

  useEffect(() => {
    const closeMenu = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, []);

  useEffect(() => {
    if (totalRecords > 0 && currentPage > lastPage) onPageChange(lastPage);
  }, [totalRecords, currentPage, lastPage, onPageChange]);

  if (!meta) return null;

  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalRecords);
  const canGoPrev = rangeStart > 1;
  const canGoNext = rangeEnd < totalRecords;

  const goToPage = target => {
    const next = Math.min(Math.max(1, target), lastPage);
    if (next === currentPage) return;
    if (
      (next > currentPage && !canGoNext) ||
      (next < currentPage && !canGoPrev)
    ) {
      return;
    }
    onPageChange(next);
  };

  return (
    <nav className="app__listing-pagination" aria-label="Pagination">
      <div className="app__listing-pagination--total text-dark">
        Total Count: {totalRecords}
      </div>
      <div className="app__listing-pagination--controls">
        <div className="app__listing-pagination--page-size-group">
          <div className="app__listing-pagination--divider" aria-hidden="true" />
          <div className="app__listing-pagination--page-size" ref={menuRef}>
            <button
              type="button"
              className="app__listing-pagination--page-size-trigger"
              onClick={() => setMenuOpen(open => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="listbox"
            >
              <span>{perPage} Records Per Page</span>
              <i
                className={classnames("fas fa-caret-down", {
                  "is-open": menuOpen
                })}
                aria-hidden="true"
              />
            </button>
            {menuOpen ? (
              <ul
                className="app__listing-pagination--page-size-menu"
                role="listbox"
              >
                {pageSizeMenuOptions.map(size => (
                  <li key={size} role="option" aria-selected={size === perPage}>
                    <button
                      type="button"
                      className={classnames({ "is-selected": size === perPage })}
                      onClick={() => {
                        setMenuOpen(false);
                        if (size !== perPage) onPageSizeChange(size);
                      }}
                    >
                      {size} Records Per Page
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="app__listing-pagination--divider" aria-hidden="true" />
        </div>
        <div className="app__listing-pagination--nav">
          <button
            type="button"
            className="app__listing-pagination--nav-btn"
            onClick={() => goToPage(1)}
            disabled={!canGoPrev}
            aria-label="First page"
          >
            <i className="fas fa-angle-double-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="app__listing-pagination--nav-btn"
            onClick={() => goToPage(currentPage - 1)}
            disabled={!canGoPrev}
            aria-label="Previous page"
          >
            <i className="fas fa-angle-left" aria-hidden="true" />
          </button>
          <span className="app__listing-pagination--range">
            {totalRecords === 0 ? "0" : `${rangeStart}-${rangeEnd}`}
          </span>
          <button
            type="button"
            className="app__listing-pagination--nav-btn"
            onClick={() => goToPage(currentPage + 1)}
            disabled={!canGoNext}
            aria-label="Next page"
          >
            <i className="fas fa-angle-right" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="app__listing-pagination--nav-btn"
            onClick={() => goToPage(lastPage)}
            disabled={!canGoNext}
            aria-label="Last page"
          >
            <i className="fas fa-angle-double-right" aria-hidden="true" />
          </button>
        </div>
      </div>
    </nav>
  );
}

ListingPagination.propTypes = {
  meta: PropTypes.shape({
    current_page: PropTypes.number,
    total_records: PropTypes.number,
    items_per_page: PropTypes.number
  }),
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number)
};

export function ReportsListingFooter({
  showLoader = false,
  apiMeta,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange
}) {
  const total = Number(apiMeta?.total_records ?? apiMeta?.total_count);
  if (
    showLoader ||
    apiMeta == null ||
    (Number.isFinite(total) ? total : -1) < 0
  ) {
    return null;
  }

  const meta = resolveReportsListingMeta(apiMeta, { page, pageSize });
  if (!meta || meta.total_records <= 0) return null;

  return (
    <div className="app__admin-listing-footer">
      <ListingPagination
        meta={meta}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

ReportsListingFooter.propTypes = {
  showLoader: PropTypes.bool,
  apiMeta: PropTypes.object,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired
};
